/**
 * Live Execution Panel — Embeds live browser view in chat area.
 *
 * Shows the live browser stream URL in an iframe or connection status.
 * Supports compact mode for embedding in SiteAnalysisDashboard.
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import {
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  FiberManualRecord as RecordIcon,
  OpenInNew as OpenIcon,
} from '@mui/icons-material';

interface LiveExecutionPanelProps {
  sessionId: string;
  qaInfraUrl: string;
  liveStreamUrl?: string;
  compact?: boolean;
}

const LiveExecutionPanel: React.FC<LiveExecutionPanelProps> = ({
  sessionId,
  qaInfraUrl,
  liveStreamUrl,
  compact = false,
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const streamUrl = liveStreamUrl || (qaInfraUrl && sessionId
    ? `${qaInfraUrl}/api/v1/sessions/${sessionId}/live`
    : null);

  if (!streamUrl) {
    return (
      <Box
        sx={{
          p: compact ? 1 : 2,
          textAlign: 'center',
          bgcolor: 'grey.100',
          borderRadius: 1,
        }}
      >
        <Typography variant="caption" color="text.secondary">
          Live browser view not available
        </Typography>
      </Box>
    );
  }

  const height = compact ? 180 : isFullscreen ? '80vh' : 400;

  return (
    <Paper
      variant="outlined"
      sx={{
        position: 'relative',
        borderRadius: 1,
        overflow: 'hidden',
        ...(isFullscreen && {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1400,
          borderRadius: 0,
        }),
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1,
          py: 0.5,
          bgcolor: 'grey.900',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RecordIcon sx={{ fontSize: 12, color: 'error.main' }} />
          <Typography variant="caption">Live Browser</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Open in new tab">
            <IconButton
              size="small"
              sx={{ color: 'white' }}
              onClick={() => window.open(streamUrl, '_blank')}
            >
              <OpenIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {!compact && (
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <IconButton
                size="small"
                sx={{ color: 'white' }}
                onClick={() => setIsFullscreen(!isFullscreen)}
              >
                {isFullscreen ? (
                  <FullscreenExitIcon fontSize="small" />
                ) : (
                  <FullscreenIcon fontSize="small" />
                )}
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {/* Stream iframe */}
      <Box sx={{ position: 'relative', height, bgcolor: 'black' }}>
        {isLoading && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              zIndex: 1,
            }}
          >
            <CircularProgress size={24} sx={{ color: 'grey.500' }} />
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'grey.500' }}>
              Connecting...
            </Typography>
          </Box>
        )}
        <iframe
          src={streamUrl}
          title="Live Browser Stream"
          style={{
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          onLoad={() => setIsLoading(false)}
          sandbox="allow-scripts allow-same-origin"
        />
      </Box>
    </Paper>
  );
};

export default LiveExecutionPanel;
